import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createAppTheme, type ColorMode, type ResolvedMode } from './createAppTheme';
import { STORAGE_KEYS } from '../config/defaults';

export interface ColorModeContextValue {
  /** The user's preference, which may be `system`. */
  mode: ColorMode;
  /** The scheme actually in effect. */
  resolvedMode: ResolvedMode;
  setMode: (mode: ColorMode) => void;
  toggleTheme: () => void;
  /** @deprecated Use `resolvedMode`. Retained for older call sites. */
  theme: ResolvedMode;
}

export const ThemeContext = createContext<ColorModeContextValue | undefined>(undefined);

const readStoredMode = (): ColorMode => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEYS.colorMode);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    return 'system';
  }
};

export interface ThemeProviderProps {
  children: ReactNode;
  /** Forces a mode; useful for tests and embedded usage. */
  defaultMode?: ColorMode;
}

export const ThemeProvider = ({ children, defaultMode }: ThemeProviderProps) => {
  const [mode, setModeState] = useState<ColorMode>(() => defaultMode ?? readStoredMode());
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });

  const resolvedMode: ResolvedMode = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEYS.colorMode, next);
    } catch {
      /* storage unavailable - preference is session-only */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setMode(resolvedMode === 'light' ? 'dark' : 'light');
  }, [resolvedMode, setMode]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-mui-color-scheme', resolvedMode);
    root.style.colorScheme = resolvedMode;

    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', resolvedMode === 'dark' ? '#0a0c12' : '#f7f8fc');
  }, [resolvedMode]);

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({ mode, resolvedMode, setMode, toggleTheme, theme: resolvedMode }),
    [mode, resolvedMode, setMode, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useColorMode = (): ColorModeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useColorMode must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;